import {Helpers} from "../../../common/js/Helpers";
import {icon, swapIcon} from "../../../common/js/icon";
import Polyfill from "../Polyfill";
import {CartProcessor} from "./CartProcessor";
import {Events} from "./CartEvents";
import {CartFeedback} from "./CartFeedback";
import {cartItemCount, cartSubtotal} from "./cartSummary";

/**
 * The selectors of the mini cart markup (snippets/cart.php and the module).
 * window.EventGalleryCartConfiguration adds removeLinkTitle, the name of the
 * remove button of an item, and removeConfirmTitle, the word its second step
 * shows ("Remove").
 */
class MiniCartOptions {
    emptyCartSelector:string = '.eventgallery-empty-minicart';
    cartSelector:string = '.eventgallery-minicart';
    cartItemsSelector:string = '.cart-items';
    cartItemSelector:string = '.cart-item';
    countSelector:string = '.item-count';
    subtotalSelector:string = '.cart-subtotal';
    statusSelector:string = '.eventgallery-minicart-status';
    removeLinkTitle:string = '';
    removeConfirmTitle:string = '';
}

/**
 * The mini cart: the module's eg-minicart card and the bar of a component page (CartBar.ts),
 * which the script fills from every cart answer.
 *
 * The items are eg-tile thumbnails (components/minicart.less) - a strip which scrolls sideways
 * in the card, a wrapping grid in the panel of the bar -, each with the quantity as a badge and
 * a remove button which is always visible; a tap on the thumbnail opens the lightbox. The remove
 * button takes the item out in two steps, as in 6.x: the first press turns the chip into a button
 * with the word "Remove" (is-confirming, its name the word), the second takes the item out -
 * all of its quantity, 25 prints of one image as well; Escape, a press elsewhere or the focus
 * leaving it turn it back (the review of #1894, UX-7; until then one tap on the chip of 24 px
 * removed the row). Every
 * count and subtotal of the cart shows the number of items and the subtotal. The cart is
 * hidden while it is empty; the module shows its empty line instead.
 *
 * A change of the cart is said in the live region of the mini cart (an
 * eg-sr-only role="status" beside the card, so it speaks while the card
 * appears or goes): "In your cart: 3 items · Subtotal ...", the words of the
 * cart line of the format dialogs. The first answer of a page, which only
 * shows what was in the cart, is not said, and of several mini carts on one
 * page (the component's and the module) only the first speaks.
 */
export class MiniCart {
    /** the mini cart of the page whose live region speaks */
    static announcer: MiniCart | null = null;

    containerElement: HTMLElement;
    cartElement: HTMLElement;
    cartItemsElement: HTMLElement;
    emptyCartElement: HTMLElement;
    statusElement: HTMLElement | null = null;
    lastStatus: string | null = null;
    options: MiniCartOptions;
    lineItems: any;
    cartProcessor: CartProcessor;
    /** the remove button which waits for its second press */
    private armed: HTMLButtonElement | null = null;

    constructor(cartProcessor: CartProcessor, containerElement: HTMLElement, options:MiniCartOptions) {
        this.containerElement = containerElement;
        this.cartProcessor = cartProcessor;
        this.lineItems = [];

        this.options = Helpers.mergeObjects(new MiniCartOptions(), options);
        // @ts-ignore
        let globalConfigStore = window.EventGalleryCartConfiguration;
        this.options = Helpers.mergeObjects(this.options, globalConfigStore);

        this.initUI();

        document.addEventListener(Events.reload, (e: CustomEvent)=>this.updateCartData(e.detail));
        document.addEventListener(Events.rebindAdd2CartButtons, () => {this.updateAdd2CartIcons()})

        // the armed remove button goes back on a press elsewhere and on Escape, which then leaves
        // the panel of the bar open
        document.addEventListener('pointerdown', (e: Event) => {
            if (this.armed && !this.armed.contains(e.target as Node)) {
                this.disarm();
            }
        }, true);
        document.addEventListener('keydown', (e: KeyboardEvent) => {
            if (this.armed && e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                this.disarm();
            }
        }, true);
    }

    initUI() {
        this.cartElement = this.containerElement?.querySelector(this.options.cartSelector);
        this.cartItemsElement = this.cartElement?.querySelector(this.options.cartItemsSelector);
        this.emptyCartElement = this.containerElement?.querySelector(this.options.emptyCartSelector);
        this.statusElement = this.containerElement?.querySelector(this.options.statusSelector) ?? null;
        if (this.statusElement && (!MiniCart.announcer || !MiniCart.announcer.containerElement?.isConnected)) {
            MiniCart.announcer = this;
        }
    }

    /**
     * Says the cart in the live region, when it changed since the last answer.
     */
    private announce(data: any) {
        if (!this.statusElement || MiniCart.announcer !== this) {
            return;
        }
        const text = CartFeedback.text(this.statusElement, data);
        if (this.lastStatus !== null && text !== this.lastStatus) {
            this.statusElement.textContent = text;
        }
        this.lastStatus = text;
    }

    _generateId(lineItem: any) {
        return 'folder=' + encodeURIComponent(lineItem.folder) + '&file=' + encodeURIComponent(lineItem.file);
    }

    /**
     * Marks the images which are in the cart.
     *
     * The icon of such an image switches from "add" to "cart" and a badge
     * shows how many items of it are in the cart, added up over all image
     * types. Every add2cart control is matched by its exact data-id: a
     * substring match would let "file=1.jpg" hit "file=11.jpg" as well.
     */
    updateAdd2CartIcons() {

        const quantities = new Map<string, number>();
        for (let i = 0; i < this.lineItems.length; i++) {
            const id = this._generateId(this.lineItems[i]);
            quantities.set(id, (quantities.get(id) ?? 0) + (parseInt(this.lineItems[i].count) || 0));
        }

        const controls = document.querySelectorAll<HTMLElement>('.eventgallery-add2cart');
        for (let i = 0; i < controls.length; i++) {
            const control = controls[i];
            const badge = this.getOrCreateBadge(control);
            const quantity = quantities.get(control.getAttribute('data-id') ?? '') ?? 0;

            // the glyph of the sprite: "add" while nothing of the image is in the cart, "cart" afterwards
            swapIcon(control.querySelector('svg.eg-icon'), quantity > 0 ? 'cart' : 'cart-plus');

            badge.textContent = quantity > 0 ? String(quantity) : '';
            badge.hidden = quantity === 0;
        }

        // Without a cart button on the thumbnail (selection only) the tick
        // control carries the badge instead.
        const toggles = document.querySelectorAll<HTMLElement>('.eventgallery-select-toggle');
        for (let i = 0; i < toggles.length; i++) {
            const toggle = toggles[i];
            const thumb = toggle.closest('.eg-tile, .thumbnail-container, .ajax-thumbnail-container, a');
            if (thumb && thumb.querySelector('.eventgallery-add2cart')) {
                continue;
            }
            const quantity = quantities.get(toggle.getAttribute('data-id') ?? '') ?? 0;
            const badge = this.getOrCreateBadge(toggle);
            badge.textContent = quantity > 0 ? String(quantity) : '';
            badge.hidden = quantity === 0;
            toggle.classList.toggle('is-in-cart', quantity > 0);
        }
    }

    /**
     * The badge is part of the server rendered markup. Controls built by a
     * script, like the one of the ajax gallery, get one on the fly.
     */
    private getOrCreateBadge(control: HTMLElement): HTMLElement {
        let badge: HTMLElement = control.querySelector('.eventgallery-add2cart-badge');
        if (!badge) {
            badge = document.createElement('span');
            badge.className = 'eg-badge eventgallery-add2cart-badge';
            badge.hidden = true;
            control.appendChild(badge);
        }
        return badge;
    }

    /**
     * Shows an element; an override of the markup from an earlier version
     * hides the cart with an inline style, which is cleared as well.
     */
    private static show(element: HTMLElement | null) {
        if (!element) return;
        element.hidden = false;
        if (element.style.display === 'none') {
            element.style.display = '';
        }
    }

    private static hide(element: HTMLElement | null) {
        if (!element) return;
        element.hidden = true;
    }

    /**
     * One item of the strip: the thumbnail of the server (a lightbox link),
     * the quantity as a badge and the remove button.
     */
    private itemMarkup(lineItem: any): string {
        const removeLabel = String(this.options.removeLinkTitle || '').replace(/"/g, '&quot;');
        const confirmLabel = String(this.options.removeConfirmTitle || this.options.removeLinkTitle || '')
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        return `<li class="eg-minicart__item cart-item" data-id="lineitemid=${lineItem.lineitemid}">
                <div class="eg-tile"><span class="eg-tile__media">${lineItem.imagetag}</span><span class="eg-badge eg-badge--photo eg-minicart__count">${lineItem.count}</span></div>
                <button type="button" class="eg-minicart__remove eventgallery-removeFromCart" aria-label="${removeLabel}">${icon('close', 'eg-icon--sm')}<span class="eg-minicart__remove-label" hidden>${confirmLabel}</span></button>
            </li>`;
    }

    /**
     * The first step of a remove button: it shows its word, which names it now, and waits for the
     * second press.
     */
    private arm(button: HTMLButtonElement) {
        this.disarm();
        this.armed = button;
        button.classList.add('is-confirming');
        button.removeAttribute('aria-label');
        const label: HTMLElement | null = button.querySelector('.eg-minicart__remove-label');
        if (label) {
            label.hidden = false;
        }
        button.addEventListener('blur', () => {
            if (this.armed === button) {
                this.disarm();
            }
        }, {once: true});
    }

    private disarm() {
        const button = this.armed;
        this.armed = null;
        if (!button) {
            return;
        }
        button.classList.remove('is-confirming');
        button.setAttribute('aria-label', String(this.options.removeLinkTitle || ''));
        const label: HTMLElement | null = button.querySelector('.eg-minicart__remove-label');
        if (label) {
            label.hidden = true;
        }
    }

    updateCartData(data:any) {

        this.lineItems = data.cart;
        if (this.lineItems.length>0) {
            MiniCart.show(this.cartElement);
            MiniCart.hide(this.emptyCartElement);
        } else {
            MiniCart.hide(this.cartElement);
            MiniCart.show(this.emptyCartElement);
        }

        let cartItemsHTML = '';
        for (let i = this.lineItems.length - 1; i >= 0; i--) {
            cartItemsHTML = cartItemsHTML + this.itemMarkup(this.lineItems[i]);
        }

        // the items are drawn anew: an armed remove button is gone with them
        this.armed = null;
        this.cartItemsElement.innerHTML = cartItemsHTML;

        this.cartItemsElement.querySelectorAll(this.options.cartItemSelector).forEach((item:HTMLElement) => {
            const deleteBtn: HTMLButtonElement = item.querySelector('.eventgallery-removeFromCart');
            if (!deleteBtn) return;
            deleteBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (this.armed !== deleteBtn) {
                    this.arm(deleteBtn);
                    return;
                }
                this.armed = null;
                this.removeCartItem(item);
            }, true);
        });

        this.updateAdd2CartIcons();

        // The badge shows the number of items, i.e. the quantities added up:
        // three prints of one image are three items. The per-image badges in
        // the strip show quantities as well, so both tell the same story.
        const itemCount = cartItemCount(data);
        // every count and subtotal of the cart: the bar of a component page shows them in the bar
        // and in its panel (CartBar.ts, which writes the cart button in the buy bar), the module once
        this.cartElement.querySelectorAll(this.options.countSelector).forEach((count: Element) => {
            count.textContent = String(itemCount);
        });

        const subtotal = cartSubtotal(data);
        this.cartElement.querySelectorAll(this.options.subtotalSelector).forEach((element: Element) => {
            element.textContent = subtotal;
        });

        this.announce(data);

        // the lightbox finds the new items by itself (PhotoSwipeGallery.onDocumentClick());
        // tigger the loading of Google Photos images for example.
        document.dispatchEvent(Polyfill.createNewEvent('eventgallery-images-added', {isOverlay: true}));
    }

    removeCartItem(elm: HTMLElement) {
        let reference:string;
        if (elm.getAttribute('data-id')) {
            reference = elm.getAttribute('data-id');
        } else {
            reference = elm.parentElement.getAttribute('data-id');
        }

        this.cartProcessor.remove(reference);
    }
}
